/**
 * One place that knows how to open a record.
 *
 * Before this existed the same switch — WO here, INSPECT there, ACTION,
 * BOOKING — was written out wherever something needed to link to a record
 * (twice inside `dashboard.component.ts` alone). Every new surface that wanted
 * to deep-link copied it again, and a route change had to be found in all of
 * them. Notifications need exactly the same knowledge, so it lives here once.
 *
 * Add a module type by adding a row to ENTITY_ROUTES. Nothing else changes.
 */

/** The record kinds anything in the product can link to. */
export type EntityModule = 'WO' | 'INSPECT' | 'ACTION' | 'BOOKING' | 'SCHEDULE' | 'EXPORT';

export interface EntityRef {
  module: EntityModule;
  /** Record id. Absent for things that are not a record (a finished export). */
  id?: string | number | null;
  /**
   * A ready-made absolute URL from the producer, when it has one the client
   * could not construct — a signed download link, a tokenised booking link.
   * Always wins over the route table.
   */
  url?: string | null;
  /** Extra context some destinations need (booking needs its schedule). */
  context?: Record<string, unknown>;
}

/** Where a link goes: an in-app route, or an external/absolute URL. */
export type EntityTarget =
  | { kind: 'route'; commands: unknown[]; queryParams?: Record<string, unknown> }
  | { kind: 'external'; href: string }
  | null;

type RouteBuilder = (ref: EntityRef) => Exclude<EntityTarget, null | { kind: 'external'; href: string }>;

/**
 * The single route table. Verified against how the product already navigates
 * (`dashboard.component.ts` and the work-order list) — not invented.
 */
const ENTITY_ROUTES: Partial<Record<EntityModule, RouteBuilder>> = {
  WO: (ref) => ({
    kind: 'route',
    commands: ['/work-orders/workOrderDetails/checklist'],
    queryParams: { workOrderId: ref.id },
  }),
  INSPECT: (ref) => ({
    kind: 'route',
    commands: ['/inspections/edit'],
    queryParams: { id: ref.id },
  }),
  ACTION: (ref) => ({
    kind: 'route',
    commands: ['/actions'],
    queryParams: { openActionId: ref.id },
  }),
  BOOKING: () => ({
    // The booking list resolves the booking from the schedule stashed in
    // session storage — see `prepareEntityNavigation`, which sets it.
    kind: 'route',
    commands: ['/schedules/booking-list'],
    queryParams: { from: 'notification' },
  }),
  SCHEDULE: () => ({
    kind: 'route',
    commands: ['/schedules/schedule-list'],
  }),
  // EXPORT is deliberately absent: a finished export is not a record you can
  // open. It carries a signed `url` instead, handled above the table.
};

/**
 * Where this reference should take the user, or null when it leads nowhere.
 *
 * A producer-supplied URL always wins — it may be signed, tokenised or point
 * outside the app, none of which the route table can reproduce.
 */
export function resolveEntityTarget(ref: EntityRef | null | undefined): EntityTarget {
  if (!ref) return null;

  const url = typeof ref.url === 'string' ? ref.url.trim() : '';
  if (url) return { kind: 'external', href: url };

  const build = ENTITY_ROUTES[ref.module];
  if (!build) return null;

  // A route keyed on an id is useless without one.
  const needsId = ref.module !== 'BOOKING' && ref.module !== 'SCHEDULE';
  if (needsId && (ref.id === null || ref.id === undefined || ref.id === '')) return null;

  return build(ref);
}

/**
 * Side effects a destination needs before navigation. Bookings are the only
 * one today: the list reads its schedule from session storage rather than the
 * URL, so a link that skips this lands on an unfiltered list.
 */
export function prepareEntityNavigation(ref: EntityRef | null | undefined): void {
  if (!ref || ref.module !== 'BOOKING') return;
  const scheduleId = ref.context?.['scheduleId'];
  if (scheduleId === undefined || scheduleId === null || scheduleId === '') return;
  try {
    sessionStorage.setItem(
      'schedule',
      JSON.stringify({ id: scheduleId, eventName: ref.context?.['eventName'] ?? '' }),
    );
  } catch {
    // Private mode or a full quota — navigation still works, just unfiltered.
  }
}

/** True when this reference leads somewhere, so a row can be rendered as actionable. */
export function isLinkable(ref: EntityRef | null | undefined): boolean {
  return resolveEntityTarget(ref) !== null;
}
